import { useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { useUser } from '@/allauth_auth/hooks.js';
import { useProfile } from '@/hooks/profile/useProfile';
import { Avatar } from '@/lib/base/avatar/avatar';
import { Button } from '@/lib/base/buttons/button';
import { Dialog, Modal, ModalOverlay } from '@/lib/base/overlays/modal';
import { SkeletonText } from '@/lib/base/skeleton/skeleton';
import { isAllowedUrl } from '@/lib/crm/utils/urlValidation';
import { EmailIframe } from '@/lib/email/EmailIframe';
import { useInlineImageHtml } from '@/lib/email/useInlineImageHtml';
import { isApiError, TimeoutError } from '@/lib/errors';
import { logger } from '@/lib/logger';
import { escapeHtml } from '@/lib/reasoning/components/email-editor/plateEmailSerializer';
import { cx } from '@/utils/cx';

import type {
  EmailPreviewPayload,
  EmailPreviewResponse,
} from './useEmailPreview';
import { useEmailPreview } from './useEmailPreview';

/**
 * Render the composed email the way the recipient's client will.
 *
 * `body_html` is the HTML alternative and is used verbatim when present. When
 * it is absent the message genuinely ships as text/plain — `_send_new_email`
 * does NOT synthesise an HTML part (only `_send_reply` does, for Gmail's
 * quote-collapsing) — and a mail client renders text/plain with its line breaks
 * intact. Feeding that raw into an HTML frame collapses every newline, so the
 * preview would show one run-on block for an email that sends correctly.
 *
 * So the plain body is escaped and its newlines converted, mirroring the
 * server's own `_plain_to_html` (html.escape + "\n" -> "<br>"). Escaping is not
 * optional: without it a body containing angle brackets would render as markup
 * in a frame that is meant to be showing text.
 */
function composedBodyToHtml(bodyHtml: string | null, body: string): string {
  if (bodyHtml) {
    return bodyHtml;
  }
  return escapeHtml(body).replace(/\n/g, '<br>');
}

/** Error codes the user can only clear by changing something outside this modal. */
const NON_RETRYABLE_ERROR_CODES = new Set([
  'draft_incomplete',
  'no_mailbox_connected',
  'reply_target_not_found',
]);

/** The per-draft signature row's resolved presentation state. */
interface SignatureRowState {
  text: string;
  action: 'remove' | 'addBack' | null;
}

/**
 * Map the composed preview + the draft's typed disposition onto the
 * signature row. Status values come from the backend SignatureStatus
 * StrEnum (apps/email/services/body_composition.py).
 */
function deriveSignatureRow(
  data: EmailPreviewResponse,
  payload: EmailPreviewPayload
): SignatureRowState {
  switch (data.signature_status) {
    case 'applied':
    case 'already_in_draft':
      return { text: 'Signature included', action: 'remove' };
    case 'disabled':
      if (payload.signature_disposition === 'user_removed') {
        return {
          text: 'You removed the signature from this email.',
          action: 'addBack',
        };
      }
      return { text: 'Signature is off for all emails.', action: null };
    case 'no_signature':
      return { text: 'No signature set', action: null };
    default:
      return data.signature_applied
        ? { text: 'Signature included', action: 'remove' }
        : { text: 'No signature included', action: null };
  }
}

/** Initials for the sender avatar, from the account email's local part. */
function senderInitials(accountEmail: string): string {
  const local = accountEmail.split('@')[0] ?? '';
  const parts = local.split(/[._-]+/).filter(Boolean);
  const first = parts[0]?.[0] ?? local[0] ?? '?';
  const second = parts[1]?.[0] ?? '';
  return `${first}${second}`.toUpperCase();
}

interface SignatureFooterProps {
  data: EmailPreviewResponse;
  payload: EmailPreviewPayload;
  orgId: number;
  signatureEnabled: boolean;
  onRemoveSignature?: () => void;
  onAddSignatureBack?: () => void;
  onNavigateToSettings: () => void;
}

/**
 * The modal's fixed footer: the per-draft signature state with its action,
 * plus a link out to Settings for the account-wide defaults. Rendering in the
 * Dialog `footer` slot keeps it visible however long the email is — the
 * defaults themselves live on the Settings page, not buried in a scroll
 * region here.
 */
function SignatureFooter({
  data,
  payload,
  orgId,
  signatureEnabled,
  onRemoveSignature,
  onAddSignatureBack,
  onNavigateToSettings,
}: SignatureFooterProps) {
  const row = deriveSignatureRow(data, payload);
  const canAddBack = Boolean(onAddSignatureBack && signatureEnabled);
  return (
    <div className='space-y-1 border-t border-[var(--color-border-secondary)] pt-3'>
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <p className='text-sm text-fg-secondary'>
          {row.text}
          {' · '}
          {data.footer_applied ? 'Ahoy footer included' : 'No Ahoy footer'}
        </p>
        {row.action === 'remove' && onRemoveSignature && (
          <Button size='sm' color='secondary' onClick={onRemoveSignature}>
            Remove signature
          </Button>
        )}
        {row.action === 'addBack' && onAddSignatureBack && (
          <Button
            size='sm'
            color='secondary'
            isDisabled={!canAddBack}
            onClick={onAddSignatureBack}
          >
            Add signature back
          </Button>
        )}
      </div>
      {row.action === 'addBack' && onAddSignatureBack && !canAddBack && (
        <p className='text-xs text-fg-tertiary'>
          Turn on your email signature in Settings to add it back.
        </p>
      )}
      <p className='text-xs text-fg-tertiary'>
        Manage defaults for all emails in{' '}
        <Link
          to={`/${orgId}/settings/email-signature`}
          onClick={onNavigateToSettings}
          className='underline hover:text-fg-primary'
        >
          Settings
        </Link>
        .
      </p>
    </div>
  );
}

interface EmailPreviewModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: number;
  payload: EmailPreviewPayload;
  onOpenThread?: () => void;
  /**
   * Remove the signature from THIS draft (sets `user_removed` on it). Omitted
   * for read-only callers — the button is hidden, the status text remains.
   */
  onRemoveSignature?: () => void;
  /**
   * Re-insert the signature into THIS draft after a removal. Only enabled
   * while the global setting is on — the send path strips it otherwise, and
   * the control must not offer a state that lies.
   */
  onAddSignatureBack?: () => void;
}

export function EmailPreviewModal({
  isOpen,
  onOpenChange,
  orgId,
  payload,
  onOpenThread,
  onRemoveSignature,
  onAddSignatureBack,
}: EmailPreviewModalProps) {
  const user = useUser();

  // Read-only here: the global setting gates the add-back control. Changing
  // the defaults happens on the Settings page the footer links to.
  const { formData, profileConfirmed } = useProfile({
    orgId,
    userId: user?.id,
  });

  // formData carries an optimistic default of email_signature_enabled: true.
  // profileConfirmed is false while the profile fetch is still pending, when
  // only a localStorage-hydrated snapshot is present, and after a refetch
  // error — in every one of those the live account setting is unknown, so
  // trusting the optimistic default would enable "Add signature back" for an
  // account whose signature is actually disabled. Gate on a profile confirmed
  // live from the network so the control defaults CLOSED until the real
  // setting is known.
  const signatureEnabled = profileConfirmed && formData.email_signature_enabled;

  const { data, isLoading, error, refetch } = useEmailPreview(orgId, payload, {
    enabled: isOpen,
  });

  // Composed HTML references inline images by file id rather than by URL — a
  // stored signature's images arrive with no `src` at all — so they have to be
  // resolved before the iframe can render them. Called unconditionally (empty
  // string until the preview loads) to keep hook order stable.
  const composedHtml = data
    ? composedBodyToHtml(data.body_html, data.body)
    : '';
  const bodyHtml = useInlineImageHtml(orgId, composedHtml);

  useEffect(() => {
    if (error) {
      let errorType = 'Unknown';
      if (error instanceof TimeoutError) {
        errorType = 'Timeout';
      } else if (isApiError(error)) {
        errorType = `ApiError:${error.statusCode}`;
      }
      logger.error(
        'Email preview fetch failed',
        error instanceof Error ? error : new Error(String(error)),
        {
          component: 'EmailPreviewModal',
          orgId,
          errorType,
        }
      );
    }
  }, [error, orgId]);

  const handleReplyRowClick = useCallback(() => {
    if (onOpenThread) {
      onOpenThread();
    } else {
      const link = data?.original_message?.external_link;
      if (link && isAllowedUrl(link)) {
        window.open(link, '_blank', 'noopener,noreferrer');
      }
    }
  }, [onOpenThread, data]);

  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  // The Settings link navigates the page under the modal — close it so the
  // user lands on Settings rather than behind an open overlay.
  const handleNavigateToSettings = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const errorBody = isApiError(error)
    ? (error.response as { error?: { code?: string } } | undefined)
    : undefined;
  const code = errorBody?.error?.code;

  const renderError = () => {
    if (code === 'draft_incomplete') {
      return (
        <div role='alert' className='space-y-1'>
          <p className='text-sm font-semibold text-fg-primary'>
            Nothing to preview yet
          </p>
          <p className='text-sm text-fg-secondary'>
            Add a recipient, a subject, and a message, then preview again.
          </p>
        </div>
      );
    }
    if (code === 'no_mailbox_connected') {
      return (
        <div role='alert' className='space-y-1'>
          <p className='text-sm font-semibold text-fg-primary'>
            No email account connected
          </p>
          <p className='text-sm text-fg-secondary'>
            Connect an email account under Integrations → Email &amp; Calendar
            to preview outgoing mail.
          </p>
        </div>
      );
    }
    if (code === 'reply_target_not_found') {
      return (
        <div role='alert' className='space-y-1'>
          <p className='text-sm font-semibold text-fg-primary'>
            Original message unavailable
          </p>
          <p className='text-sm text-fg-secondary'>
            We couldn&apos;t find the message this email replies to. It may have
            been deleted or moved.
          </p>
        </div>
      );
    }
    return (
      <div role='alert' className='space-y-1'>
        <p className='text-sm font-semibold text-fg-primary'>
          Preview didn&apos;t load
        </p>
        <p className='text-sm text-fg-secondary'>
          Something went wrong building this preview.
        </p>
      </div>
    );
  };

  // Retry only helps for transient failures. The coded states are terminal until
  // the user changes something outside this modal (the draft, their integrations,
  // the thread), so they get copy telling them what to change instead of a button
  // that would reproduce the same error.
  const showRetry =
    !isLoading && Boolean(error) && !NON_RETRYABLE_ERROR_CODES.has(code ?? '');

  const isReady = !isLoading && !error && data !== undefined;

  // The footer is chrome: fixed below the scrollable body in every state that
  // has actions. Ready → the signature controls; retryable error → Try again;
  // loading / terminal errors → omitted (the body supplies its own bottom
  // padding per the Dialog padding contract).
  let footer: React.ReactNode;
  if (isReady && data) {
    footer = (
      <SignatureFooter
        data={data}
        payload={payload}
        orgId={orgId}
        signatureEnabled={signatureEnabled}
        onRemoveSignature={onRemoveSignature}
        onAddSignatureBack={onAddSignatureBack}
        onNavigateToSettings={handleNavigateToSettings}
      />
    );
  } else if (showRetry) {
    footer = <Button onClick={handleRetry}>Try again</Button>;
  } else {
    footer = undefined;
  }

  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={onOpenChange}>
      <Modal className='max-w-2xl w-full'>
        <Dialog
          aria-label='Email preview'
          title='Email preview'
          instance='email-preview'
          footer={footer}
        >
          {/* The Dialog's footer region supplies the modal's bottom padding.
              In the states that render no footer (loading, terminal errors)
              the body must supply it — see the Dialog padding contract. */}
          <div className={cx('space-y-4', footer === undefined && 'pb-6')}>
            {isLoading && (
              <div aria-busy='true'>
                <SkeletonText lines={6} />
              </div>
            )}
            {!isLoading && error && renderError()}
            {isReady && data && (
              <div className='space-y-3'>
                {/* Reading-pane addressing: subject prominent, sender +
                    recipients beneath. Scrolls with the message under the
                    Dialog's fixed title chrome — the chrome regions (title
                    above, signature controls below) are the parts that must
                    never move. */}
                <div className='space-y-2 border-b border-[var(--color-border-secondary)] pb-3'>
                  <h3 className='truncate text-md font-semibold text-fg-primary'>
                    {data.subject}
                  </h3>
                  <div className='flex items-start gap-3'>
                    <Avatar
                      size='sm'
                      alt={data.account_email}
                      initials={senderInitials(data.account_email)}
                    />
                    <div className='min-w-0 flex-1 space-y-0.5 text-sm text-fg-secondary'>
                      <p className='truncate'>
                        <span className='font-medium text-fg-primary'>
                          From
                        </span>{' '}
                        {data.account_email}
                      </p>
                      <p className='truncate'>
                        <span className='font-medium text-fg-primary'>To</span>{' '}
                        {payload.to.join(', ')}
                      </p>
                      {payload.cc && payload.cc.length > 0 && (
                        <p className='truncate'>
                          <span className='font-medium text-fg-primary'>
                            Cc
                          </span>{' '}
                          {payload.cc.join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {data.is_reply && data.original_message && (
                  <button
                    type='button'
                    onClick={handleReplyRowClick}
                    aria-label='Open original message'
                    className='w-full text-left text-sm text-fg-secondary min-h-6 px-3 py-2 rounded hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-brand'
                  >
                    <span className='font-medium text-fg-primary'>
                      In reply to:
                    </span>{' '}
                    {data.original_message.subject} –{' '}
                    {data.original_message.sender}
                  </button>
                )}

                {/* The composed email — the thing being previewed — framed as a
                    letter surface. Given real vertical space so a short email
                    still reads as an email; EmailIframe self-sizes above this
                    floor.

                    maxHeight={null} disables the frame's internal clamp: the
                    email renders at its natural height and the Dialog BODY is
                    the single scroll context. With the default 400px clamp a
                    long email became a nested scroll trap — wheel over the
                    message scrolled inside the frame first, then chained to
                    the modal, with two competing scrollbars.

                    An unedited draft has no stored HTML part, so the composed
                    body_html is null while `body` still carries the content —
                    see composedBodyToHtml for why that has to be escaped and
                    line-broken rather than passed through as markup. */}
                <div className='overflow-hidden rounded-lg border border-[var(--color-border-secondary)]'>
                  <EmailIframe
                    html={bodyHtml}
                    minHeight='320px'
                    maxHeight={null}
                  />
                </div>
              </div>
            )}
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
